import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import appConfig from "../../config";

export const signIn = createAsyncThunk("SIGN_IN", async (body) => {
  try {
    const { data } = await axios.post(`${appConfig.baseUrl}/admins/sign_in`, body);
    return data;
  } catch (err) {
    throw err.response.data;
  }
});

// Which sign-in methods the server offers: { password_login, slack_sso }.
export const fetchAuthOptions = createAsyncThunk("FETCH_AUTH_OPTIONS", async () => {
  try {
    const { data } = await axios.get(`${appConfig.baseUrl}/admins/auth_options`);
    return data;
  } catch (err) {
    throw err.response?.data || err.message;
  }
});

// The signed `state` is kept for the callback page to compare against what
// Slack sends back, so a callback link planted by someone else is rejected.
export const SLACK_STATE_KEY = "haazri.slackSignInState";

// The workspace the admin last signed in from, so Slack skips its workspace
// picker. Storage can be blocked, in which case Slack just asks as before.
const SLACK_TEAM_KEY = "haazri.lastSlackTeam";

const readLastSlackTeam = () => {
  try {
    return localStorage.getItem(SLACK_TEAM_KEY) || undefined;
  } catch (err) {
    return undefined;
  }
};

const saveLastSlackTeam = (teamId) => {
  try {
    if (teamId) localStorage.setItem(SLACK_TEAM_KEY, teamId);
  } catch (err) {
    // ignore
  }
};

export const startSlackSignIn = createAsyncThunk("START_SLACK_SIGN_IN", async () => {
  try {
    const { data } = await axios.get(`${appConfig.baseUrl}/admins/sso/slack/start`, {
      params: { team: readLastSlackTeam() },
    });
    sessionStorage.setItem(SLACK_STATE_KEY, data.state);
    window.location.assign(data.url);
  } catch (err) {
    throw err.response?.data || err.message;
  }
});

export const slackSignIn = createAsyncThunk("SLACK_SIGN_IN", async (body) => {
  try {
    const { data } = await axios.post(`${appConfig.baseUrl}/admins/sso/slack/callback`, body);
    saveLastSlackTeam(data.slack_team_id);
    return data;
  } catch (err) {
    throw err.response?.data || err.message;
  }
});

// Refreshes the admin + permissions from the server so role changes apply
// without a re-login (and sessions saved before roles existed get permissions).
export const fetchMe = createAsyncThunk("FETCH_ME", async (adminId, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`/admins/${adminId}/me`);
    return data;
  } catch (err) {
    return rejectWithValue({ status: err.response?.status });
  }
});

// Platform admins only: acts in another organization. The header is read from
// the store by the request interceptor, so it is set before the workspace is
// fetched. Everything else on screen is reloaded by the caller.
export const switchOrganization = createAsyncThunk(
  "SWITCH_ORGANIZATION",
  async ({ adminId, organizationId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/admins/${adminId}/workspace`, {
        headers: { "X-Organization-Id": organizationId },
      });
      return data;
    } catch (err) {
      return rejectWithValue({ status: err.response?.status });
    }
  }
);
